import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import { SERVICE_CATEGORIES } from '../serviceCategories';

const colors = {
  bg: '#F7F5F0',
  surface: '#fff',
  border: '#E3E1D9',
  text: '#1F1E1B',
  textMuted: '#5F5E5A',
  primary: '#0F6E56',
};

const API_BASE = '/api/services';

function Stars({ rating }) {
  const full = Math.round(rating || 0);
  return (
    <span style={{ color: '#D9A400', fontSize: 13 }}>
      {'★'.repeat(full)}{'☆'.repeat(5 - full)}
    </span>
  );
}

function ProviderCard({ p }) {
  const waLink = p.whatsapp ? `https://wa.me/${p.whatsapp.replace(/[^0-9]/g, '')}` : null;
  return (
    <div style={{
      background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 14,
      padding: '1rem', marginBottom: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: colors.text }}>
            {p.category_icon} {p.business_name}
            {p.is_verified ? <span style={{ color: colors.primary, marginLeft: 6, fontSize: 12 }}>✔ {t('verified')}</span> : null}
          </p>
          <p style={{ margin: '2px 0', fontSize: 12, color: colors.textMuted }}>
            {p.category_display} · {p.city}{p.area ? `, ${p.area}` : ''}
          </p>
          <p style={{ margin: '2px 0', fontSize: 12 }}>
            <Stars rating={p.avg_rating} /> <span style={{ color: colors.textMuted }}>({p.reviews_count})</span>
            {p.distance_km != null ? <span style={{ color: colors.textMuted }}> · {p.distance_km} km</span> : null}
          </p>
        </div>
        {p.hourly_rate_pi ? (
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: colors.primary }}>
            π{p.hourly_rate_pi}/{t('hr')}
          </p>
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        {p.phone ? (
          <a href={`tel:${p.phone}`} style={{ flex: 1, textAlign: 'center', padding: '8px 0', borderRadius: 10, background: colors.bg, color: colors.text, fontSize: 12, textDecoration: 'none', border: `0.5px solid ${colors.border}` }}>
            📞 {t('call')}
          </a>
        ) : null}
        {waLink ? (
          <a href={waLink} target="_blank" rel="noreferrer" style={{ flex: 1, textAlign: 'center', padding: '8px 0', borderRadius: 10, background: colors.bg, color: colors.text, fontSize: 12, textDecoration: 'none', border: `0.5px solid ${colors.border}` }}>
            💬 {t('whatsapp')}
          </a>
        ) : null}
        <a href={`/services/${p.id}`} style={{ flex: 1, textAlign: 'center', padding: '8px 0', borderRadius: 10, background: colors.primary, color: '#fff', fontSize: 12, textDecoration: 'none' }}>
          {t('view_profile')}
        </a>
      </div>
    </div>
  );
}

export default function Services() {
  const [providers, setProviders] = useState(null);
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');
  const [q, setQ] = useState('');
  const [coords, setCoords] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (city) params.set('city', city);
      if (q) params.set('q', q);
      if (coords) { params.set('lat', coords.lat); params.set('lng', coords.lng); }
      const res = await fetch(`${API_BASE}/providers/?${params.toString()}`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setProviders(Array.isArray(data.providers) ? data.providers : []);
        setErr('');
      } else {
        setProviders([]);
        setErr(data.error || `${t('could_not_load_providers')} (${res.status})`);
      }
    } catch (e) {
      setProviders([]);
      setErr(t('could_not_reach_server'));
    }
  }, [category, city, q, coords]);

  useEffect(() => { load(); }, [load]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
    );
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '1.5rem', background: colors.bg, minHeight: '100vh', fontFamily: 'sans-serif' }}>
      <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>🧰 {t('services')}</h1>
      <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 1rem' }}>
        {t('services_sub')}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: '1rem' }}>
        {SERVICE_CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setCategory(category === c.key ? '' : c.key)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
              padding: '10px 4px', borderRadius: 12, cursor: 'pointer',
              border: category === c.key ? `1.5px solid ${colors.primary}` : `0.5px solid ${colors.border}`,
              background: category === c.key ? '#EAF6F2' : colors.surface,
            }}
          >
            <span style={{ fontSize: 20 }}>{c.icon}</span>
            <span style={{ fontSize: 10, color: colors.text, textAlign: 'center' }}>{t(c.key) !== c.key ? t(c.key) : c.label}</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
        <input
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder={t('city_placeholder')}
          style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: `0.5px solid ${colors.border}`, fontSize: 13 }}
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('search_placeholder')}
          style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: `0.5px solid ${colors.border}`, fontSize: 13 }}
        />
        <button
          onClick={useMyLocation}
          style={{ padding: '0 14px', borderRadius: 10, border: `0.5px solid ${colors.border}`, background: colors.surface, fontSize: 13, cursor: 'pointer' }}
        >
          📍
        </button>
      </div>

      <a href="/services/register" style={{ display: 'block', textAlign: 'center', padding: '10px 0', borderRadius: 10, background: colors.primary, color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none', marginBottom: '1.2rem' }}>
        + {t('become_a_service_provider')}
      </a>

      {providers === null ? (
        <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading')}</p>
      ) : providers.length === 0 ? (
        <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{err || t('no_providers_found')}</p>
      ) : (
        providers.map((p) => <ProviderCard key={p.id} p={p} />)
      )}
    </div>
  );
}
